/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Injectable} from '@angular/core';
import {BsModalRef, BsModalService, ModalOptions} from 'ngx-bootstrap/modal';

import {copyModalPageFormProviders, isCopyModalComponent} from '../util/isolate-copy-modal-page-form';

@Injectable({providedIn: 'root'})
export class AngularModalService {
  constructor(private readonly bsModalService: BsModalService) {
  }

  show<T>(content: any, config?: ModalOptions<T>): BsModalRef<T> {
    // Defaults first so callers can override (e.g. keyboard: false on confirm dialogs).
    const options: ModalOptions<T> = {
      keyboard: true,
      ...config
    };
    if (isCopyModalComponent(content)) {
      options.providers = [...(options.providers || []), ...copyModalPageFormProviders()];
    }
    return this.bsModalService.show(content, options);
  }

  /**
   * Open after the current zoneless tick. Use when the caller is a popover
   * click or other in-flight CD — a plain setTimeout(0) often runs first and
   * ngx-bootstrap then creates the modal mid-check (NG0100). The returned
   * promise rejects if deferred modal creation fails. Pass the requesting
   * view's DestroyRef to skip opening after that view has been destroyed.
   */
  showAfterTick<T>(
    content: any,
    config?: ModalOptions<T>
  ): Promise<BsModalRef<T>>;
  showAfterTick<T>(
    content: any,
    config: ModalOptions<T> | undefined,
    destroyRef: DestroyRef
  ): Promise<BsModalRef<T> | null>;
  async showAfterTick<T>(
    content: any,
    config?: ModalOptions<T>,
    destroyRef?: DestroyRef
  ): Promise<BsModalRef<T> | null> {
    await new Promise<void>((resolve) => {
      queueMicrotask(() => setTimeout(resolve, 0));
    });
    if (destroyRef?.destroyed) {
      return null;
    }
    return this.show(content, config);
  }
}
